/*
 * Title: Reconcile In Bulk - ImportPreviewModal
 * Description: Modal for previewing imported Excel changes with checkboxes
 */

import { useEffect, useMemo } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { useReconcileInBulk } from './ContextAPI';

function ImportPreviewModal() {
    const {
        showImportModal,
        importPreviewItems,
        importExecuting,
        toggleImportItem,
        toggleAllImportItems,
        closeImportModal,
        executeBulk,
        lossReasons,
        lossReasonId,
        lossRemark,
        lossDate,
        fetchLossReasons,
        setLossReasonId,
        setLossRemark,
        setLossDate,
    } = useReconcileInBulk();

    const changedItems = useMemo(
        () => importPreviewItems.filter(i => i.status === 'changed'),
        [importPreviewItems]
    );

    const checkedItems = useMemo(
        () => changedItems.filter(i => i.checked),
        [changedItems]
    );

    const hasLossItems = useMemo(
        () => checkedItems.some(i => i.type === 'loss'),
        [checkedItems]
    );

    const allChecked = changedItems.length > 0 && checkedItems.length === changedItems.length;
    const someChecked = checkedItems.length > 0 && !allChecked;

    useEffect(() => {
        if (showImportModal) {
            fetchLossReasons();
        }
    }, [showImportModal, fetchLossReasons]);

    if (!showImportModal) return null;

    const handleExecute = async () => {
        if (!checkedItems.length) return;
        if (hasLossItems && !lossReasonId) return;
        await executeBulk(checkedItems);
    };

    const totalGain = checkedItems.filter(i => i.type === 'gain').reduce((a, i) => a + (i.diff || 0), 0);
    const totalLoss = checkedItems.filter(i => i.type === 'loss').reduce((a, i) => a + Math.abs(i.diff || 0), 0);

    return (
        <Modal show={showImportModal} onHide={closeImportModal} size="xl" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    Import Preview
                    <Badge bg="info" className="ms-2">{importPreviewItems.length} rows</Badge>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {importPreviewItems.length === 0 ? (
                    <p className="text-muted text-center py-4">No data to preview.</p>
                ) : (
                    <>
                        {hasLossItems && (
                            <Card className="mb-3 border-warning">
                                <Card.Header className="bg-warning text-dark py-2">
                                    <strong>Loss Request Configuration</strong>
                                </Card.Header>
                                <Card.Body className="py-3">
                                    <Row className="g-3">
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label>Loss Reason <span className="text-danger">*</span></Form.Label>
                                                <Form.Select
                                                    size="sm"
                                                    value={lossReasonId || ''}
                                                    onChange={(e) => setLossReasonId(parseInt(e.target.value, 10) || null)}
                                                    required
                                                >
                                                    <option value="">Select Reason</option>
                                                    {lossReasons.map(r => (
                                                        <option key={r.id} value={r.id}>{r.title}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label>Remarks <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={lossRemark}
                                                    onChange={(e) => setLossRemark(e.target.value)}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label>Date <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    size="sm"
                                                    value={lossDate}
                                                    onChange={(e) => setLossDate(e.target.value)}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        )}

                        <div className="mb-3 d-flex gap-3 flex-wrap">
                            <Badge bg="secondary">Total: {importPreviewItems.length}</Badge>
                            <Badge bg="success">Gain: {checkedItems.filter(i => i.type === 'gain').length} (+{totalGain.toLocaleString()})</Badge>
                            <Badge bg="danger">Loss: {checkedItems.filter(i => i.type === 'loss').length} (-{totalLoss.toLocaleString()})</Badge>
                            <Badge bg="info">Unchanged: {importPreviewItems.filter(i => i.status === 'unchanged').length}</Badge>
                            <Badge bg="warning">Rejected: {importPreviewItems.filter(i => i.status === 'rejected').length}</Badge>
                        </div>

                        <Table striped hover bordered size="sm">
                            <thead className="table-light sticky-top">
                                <tr>
                                    <th style={{ width: '40px' }}>
                                        <Form.Check
                                            type="checkbox"
                                            checked={allChecked}
                                            onChange={(e) => toggleAllImportItems(e.target.checked)}
                                        />
                                        {someChecked && <span className="text-muted small d-block">partial</span>}
                                    </th>
                                    <th style={{ width: '80px' }}>Detail ID</th>
                                    <th style={{ width: '80px' }}>Code</th>
                                    <th>Drug Name</th>
                                    <th style={{ width: '100px' }}>PO No</th>
                                    <th style={{ width: '100px' }}>Batch</th>
                                    <th style={{ width: '100px' }} className="text-end">Original Qty</th>
                                    <th style={{ width: '100px' }} className="text-end">New Qty</th>
                                    <th style={{ width: '80px' }} className="text-end">Diff</th>
                                    <th style={{ width: '80px' }}>Type</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {importPreviewItems.map((item, index) => (
                                    <tr
                                        key={item.detail_id || `import-${index}`}
                                        className={
                                            item.status === 'rejected' ? 'table-warning'
                                                : item.type === 'gain' ? 'table-success'
                                                    : item.type === 'loss' ? 'table-danger'
                                                        : ''
                                        }
                                    >
                                        <td>
                                            <Form.Check
                                                type="checkbox"
                                                checked={item.checked || false}
                                                disabled={item.status !== 'changed'}
                                                onChange={() => toggleImportItem(index)}
                                            />
                                        </td>
                                        <td>{item.detail_id}</td>
                                        <td>{item.code_no || '-'}</td>
                                        <td>{item.drug_name || '-'}</td>
                                        <td>{item.po_no || '-'}</td>
                                        <td>{item.batch_no || '-'}</td>
                                        <td className="text-end">{item.original_qty?.toLocaleString() || '-'}</td>
                                        <td className="text-end fw-bold">{item.new_qty?.toLocaleString() || '-'}</td>
                                        <td className={`text-end fw-bold ${item.type === 'gain' ? 'text-success' : item.type === 'loss' ? 'text-danger' : ''}`}>
                                            {item.diff != null ? (item.diff > 0 ? '+' : '') + item.diff.toLocaleString() : '-'}
                                        </td>
                                        <td>
                                            {item.type === 'gain' && <Badge bg="success">Gain</Badge>}
                                            {item.type === 'loss' && <Badge bg="danger">Loss</Badge>}
                                            {item.type === 'equal' && <Badge bg="info">Equal</Badge>}
                                        </td>
                                        <td>
                                            {item.status === 'rejected' && (
                                                <Badge bg="warning" text="dark">{item.reason}</Badge>
                                            )}
                                            {item.status === 'unchanged' && (
                                                <Badge bg="secondary">No change</Badge>
                                            )}
                                            {item.status === 'changed' && (
                                                <Badge bg="success">Ready</Badge>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeImportModal} disabled={importExecuting}>
                    Cancel
                </Button>
                <Button
                    variant="danger"
                    onClick={handleExecute}
                    disabled={importExecuting || checkedItems.length === 0 || (hasLossItems && !lossReasonId)}
                >
                    {importExecuting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" /> Executing...
                        </>
                    ) : (
                        `Execute Checked (${checkedItems.length} items)`
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default ImportPreviewModal;
